
import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { customerService } from "@/services/api/cresivoxServer";
import { REDUCER_NAME, iCustomerDTO } from './Types';

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

export const createCustomer = createAsyncThunk<
  iGenericResponse<any | null> | null,
  iCustomerDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("createCustomer"),
  // @ts-expect-error
  async (payload, { rejectWithValue }) => {
    try {
      const response = await customerService.createCustomer(payload);
      if (response) {
        const { httpStatusCode, data, message } = response;
        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                data: {
                  data: data.data,
                  message,
                }
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          case httpStatusCodes.SUCCESS_CREATED: {
            if (data && data.data) {
              return {
                data: {
                  data: data.data,
                  message,
                }
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  },
);

export const updateCustomer = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { customerId: string, data: iCustomerDTO },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("updateCustomer"),
  // @ts-expect-error
  async ({ customerId, data }, { rejectWithValue }) => {
    try {
      const response = await customerService.updateCustomer(customerId, data);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                data: {
                  data: data.data,
                  message,
                }
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  },
);

export const getCustomerById = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { customerId: string },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getCustomerById"),
  // @ts-expect-error
  async (arg, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomerById(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;
        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                data: {
                  customer: data.data,
                  message,
                }
              }
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  },
);

// UPDATED: getCustomerList with search parameter
export const getCustomerList = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { search?: string; page?: number; limit?: number } | void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("customerList"),
  // @ts-expect-error
  async (arg, { rejectWithValue }) => {
    try {
      const search = arg && typeof arg === 'object' ? arg.search : undefined;
      const page = arg && typeof arg === 'object' ? arg.page : undefined;
      const limit = arg && typeof arg === 'object' ? arg.limit : undefined;

      const response = search 
        ? await customerService.searchCustomers(search, page, limit)
        : await customerService.getAllCustomer(page, limit);

      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              const customerData = Array.isArray(data.data)
                ? data.data
                : data.data.customers || data.data.data || [];

              const pagination = Array.isArray(data.data)
                ? null
                : data.data.pagination
                  ? {
                      page: data.data.pagination.page ?? data.data.pagination.currentPage ?? 1,
                      limit: data.data.pagination.limit ?? 10,
                      totalPages: data.data.pagination.totalPages ?? 1,
                      totalCount: data.data.pagination.totalCount ?? data.data.pagination.count ?? 0,
                    }
                  : null;

              return {
                data: {
                  httpStatusCode,
                  data: customerData,
                  pagination,
                  message,
                }
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  },
);

export const deleteCustomer = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { customerId: string },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("deleteCustomer"),
  // @ts-expect-error
  async ({ customerId }, { rejectWithValue }) => {
    try {
      const response = await customerService.deleteCustomer(customerId);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                data: data.data,
                message,
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  },
);
